/**
 * Application configuration management
 * Uses validated environment variables for better error handling
 */

import { DEFAULT_VIDEO_LINK_DOMAIN } from './constants';
import { getEnvConfig } from './envValidation';

export interface AppConfig {
  apiBaseUrl: string;
  videoLinkDomain: string;
  environment: string;
  enableAnalytics: boolean;
  enableDebug: boolean;
  enableI18n: boolean;
  googleRecaptchaSiteKey: string;
  // Mixpanel
  mixpanelToken?: string;
  mixpanelApiHost?: string; // hostname only
  mixpanelApiEndpoint?: string; // path only
  appName: string;
  region?: string;
  // Chatbot-specific configuration
  chatbotApiKey: string;
  chatbotTimeoutMs: number;
  chatbotRetryAttempts: number;
}

/**
 * Get application configuration from validated environment variables
 * @returns Application configuration object
 */
export const getAppConfig = (): AppConfig => {
  const envConfig = getEnvConfig();

  return {
    apiBaseUrl: envConfig.apiBaseUrl,
    videoLinkDomain: process.env.REACT_APP_VIDEO_LINK_DOMAIN || DEFAULT_VIDEO_LINK_DOMAIN,
    environment: envConfig.environment,
    enableAnalytics: envConfig.enableAnalytics,
    enableDebug: envConfig.enableDebug,
    enableI18n: envConfig.enableI18n,
    googleRecaptchaSiteKey: envConfig.googleRecaptchaSiteKey,
    // Mixpanel
    mixpanelToken: envConfig.mixpanelToken,
    mixpanelApiHost: envConfig.mixpanelApiHost,
    mixpanelApiEndpoint: envConfig.mixpanelApiEndpoint,
    appName: envConfig.appName || 'Fusus Core Setup',
    region: envConfig.region,
    chatbotApiKey: envConfig.chatbotApiKey || 'dev-chatbot-key',
    chatbotTimeoutMs: envConfig.chatbotTimeoutMs || 60000,
    chatbotRetryAttempts: envConfig.chatbotRetryAttempts || 3,
  };
};

/**
 * Generate video link URL for a given serial number
 * @param serial - Device serial number
 * @returns Full video link URL
 */
export const getVideoLinkUrl = (serial: string): string => {
  if (!serial || serial.trim() === '') {
    throw new Error('Serial number is required to generate video link URL');
  }
  
  const config = getAppConfig();
  const sanitizedSerial = serial.trim().toLowerCase();
  
  return `https://${sanitizedSerial}.${config.videoLinkDomain}`;
};

/**
 * Check if current environment is production
 * @returns true if production environment
 */
export const isProduction = (): boolean => {
  return getAppConfig().environment === 'production';
};

/**
 * Check if current environment is development
 * @returns true if development environment
 */
export const isDevelopment = (): boolean => {
  return getAppConfig().environment === 'development';
};

/**
 * Check if current environment is test
 * @returns true if test environment
 */
export const isTest = (): boolean => {
  return getAppConfig().environment === 'test';
};

/**
 * Check if analytics is enabled
 * @returns true if analytics should be enabled
 */
export const isAnalyticsEnabled = (): boolean => {
  return getAppConfig().enableAnalytics;
};

/**
 * Check if debug mode is enabled
 * @returns true if debug mode is enabled
 */
export const isDebugEnabled = (): boolean => {
  return getAppConfig().enableDebug;
};

/**
 * Check if i18n is enabled
 * @returns true if internationalization is enabled
 */
export const isI18nEnabled = (): boolean => {
  return getAppConfig().enableI18n;
};

/**
 * Get chatbot API key
 * @returns chatbot API key
 */
export const getChatbotApiKey = (): string => {
  return getAppConfig().chatbotApiKey;
};

/**
 * Get chatbot timeout in milliseconds
 * @returns timeout value in milliseconds
 */
export const getChatbotTimeoutMs = (): number => {
  return getAppConfig().chatbotTimeoutMs;
};

/**
 * Get chatbot retry attempts
 * @returns number of retry attempts
 */
export const getChatbotRetryAttempts = (): number => {
  return getAppConfig().chatbotRetryAttempts;
};